import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Drawer, List, CssBaseline, Divider, Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import MenuItem from './components/menu-item';
import logo from '../../assets/images/header-logo.png';
import useStyles from './styles';
import ToggleArrowDrawer from './components/toggle-arrow-drawer';
import { AppState } from '../../store';
import { setSessionHistory } from '../../store/user-preferences-v2/actions';
import { NavSection } from '../../globals/nav-list';

type SideNavProperties = { children: React.ReactNode; navList: NavSection };

const getNavLink = (queryString: string) => {
  const parameters = new URLSearchParams(queryString);
  if (queryString) {
    parameters.set('latest', 'true');
  }
  return `?${parameters.toString()}`;
};

const SideNav = ({ children, navList }: SideNavProperties) => {
  const search = useSelector((state: AppState) => state.search.selectedEntity);
  const sessionHistory = useSelector((state: AppState) => state.userPreference.sessionHistory);
  const vimsSearch = useSelector((state: AppState) => state.search.selectedVIMSEntity)

  const classes = useStyles();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const queryString = decodeURIComponent(location.search);
  const { deviceId, page }: { deviceId?: string; page?: string } = useParams();
  const active = (key: string) => page === key;
  const handleDrawerToggle = () => {
    setOpen(!open);
  };
  const vimsSftpUserId = localStorage.getItem('vimsSftpUserId');

  const rememberDevice = useCallback(() => {
    if (location.pathname !== '/redirect') {
      dispatch(setSessionHistory({ page, deviceId, queryString }));
      const pathName = location.pathname;
      const savedPath = window.localStorage.getItem('saved-path');
      const savedSearch = window.localStorage.getItem('saved-search');
      if (savedPath !== pathName || savedSearch !== location.search) {
        localStorage.setItem('saved-path', pathName);
        localStorage.setItem('saved-search', location.search);
      }
    }
  }, [deviceId, dispatch, location.pathname, location.search, page, queryString]);

  useEffect(() => {
    rememberDevice();
  }, [location, rememberDevice]);


  const carryDevice = vimsSftpUserId || search?.deviceId || '';

  const getMenuUri = (navItem: any) => {
    const uri =
      navItem.carryDevice ?
        (navItem.key === 'transactions')
          ? `${navItem.uri}/${carryDevice}${sessionHistory &&
            sessionHistory[navItem.key] &&
            (navItem.key === 'transactions' || navItem.key === 'reports')
            ? getNavLink(sessionHistory[navItem.key].queryString)
            : ''
          }`
          : (navItem.key === 'vims-transactions') 
          ? `${navItem.uri}/${vimsSearch?.telematicsAssetId || ''}${sessionHistory &&
            sessionHistory[navItem.key] &&
            (navItem.key === 'vims-transactions' || navItem.key === 'reports')
            ? getNavLink(sessionHistory[navItem.key].queryString)
            : ''
            }`
            : `${navItem.uri}/${search?.deviceId || ''}${sessionHistory &&
              sessionHistory[navItem.key] &&
              (navItem.key === 'transactions' || navItem.key === 'vims-transactions' || navItem.key === 'reports')
              ? getNavLink(sessionHistory[navItem.key].queryString)
              : ''
            }`
        : navItem.uri;

    return uri;
  };

  const resetTransactions = () => {
    localStorage.removeItem('transaction-table-sort-by');
    localStorage.removeItem('transaction-table-column-widths');
    localStorage.removeItem('vims-transaction-table-sort-by');
    localStorage.removeItem('vims-transaction-table-column-widths');
  }

  return (
    <Box display="flex" height="100%">
      <CssBaseline />
      <Drawer
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
      >
        <Link className={classes.toolbar} to="/">
          <img className={classes.logo} src={logo} alt="cat logo" onClick={() => resetTransactions()} onKeyDown={resetTransactions} aria-hidden />
        </Link>
        <Divider />
        <List style={{ padding: 0 }}>
          {Object.entries(navList).map(([key, navItem]) => (
            <MenuItem
              key={`${navItem.title}-menu-item`}
              active={active(key)}
              navItem={navItem}
              uri={getMenuUri(navItem)}
              open={open}
            />
          ))}
        </List>
        <ToggleArrowDrawer handleClick={handleDrawerToggle} active={open} />
      </Drawer>
      <main className={classes.content}>{children}</main>
    </Box>
  );
};

export default SideNav;
